import React from "react";
import { alpha } from "@mui/material/styles";
import { Alert, Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";

function formatDateDisplay(iso) {
  if (!iso) return "";
  const s = String(iso).slice(0, 10);
  const [y, m, d] = s.split("-");
  if (!y || !m || !d) return s;
  return `${d}/${m}/${y}`;
}

function sourceLabel(source, t) {
  const key = `bpeWizard.suggestionSource.${source}`;
  const translated = t(key);
  return translated !== key ? translated : t("bpeWizard.suggestionSource.unknown");
}

/** Texto em linguagem simples explicando por que a sugestão foi feita. */
function basisText(suggestions, t) {
  const basis = suggestions?.basis || {};
  switch (suggestions?.source) {
    case "tags":
      return t("bpeWizard.suggestionBasis.tags");
    case "client_history":
      return t("bpeWizard.suggestionBasis.clientHistory", {
        count: basis.clientPurchases || 0,
      });
    case "global_history":
      return t("bpeWizard.suggestionBasis.globalHistory");
    case "client_profile":
      return t("bpeWizard.suggestionBasis.clientProfile");
    default:
      return "";
  }
}

const CONFIDENCE_COLOR = { high: "success", medium: "info", low: "default" };

function confidenceChip(confidence, t) {
  if (!confidence || confidence === "none") return null;
  const label = t(`bpeWizard.suggestionConfidence.${confidence}`);
  return (
    <Chip
      size="small"
      color={CONFIDENCE_COLOR[confidence] || "default"}
      variant={confidence === "low" ? "outlined" : "filled"}
      label={label}
      sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
    />
  );
}

function buildStepMessage(step, suggestions, t) {
  if (!suggestions || suggestions.source === "none") return null;
  const route = suggestions.route;
  if (step === 1 && suggestions.preferredDepartureTime) {
    return t("bpeWizard.suggestionTrip", { time: suggestions.preferredDepartureTime });
  }
  if (step === 3 && suggestions.passenger?.nome) {
    return t("bpeWizard.suggestionPassenger", { name: suggestions.passenger.nome });
  }
  if (step === 4 && suggestions.paymentMethodName) {
    return t("bpeWizard.suggestionPayment", { method: suggestions.paymentMethodName });
  }
  if (step === 0 && route?.origem && route?.destino) {
    return t("bpeWizard.suggestionRoute", {
      origem: route.origem,
      destino: route.destino,
      date: formatDateDisplay(suggestions.travelDate),
      days: suggestions.avgAdvanceDays ?? "—",
    });
  }
  return null;
}

/** Itens que serão aplicados automaticamente (mostrados no card consolidado da etapa 0). */
function buildPlanItems(suggestions, t) {
  const items = [];
  const route = suggestions?.route;
  if (route?.origem && route?.destino) {
    items.push({
      icon: <RouteOutlinedIcon fontSize="inherit" />,
      text: `${route.origem} → ${route.destino}`,
    });
  } else if (route?.origem) {
    items.push({ icon: <RouteOutlinedIcon fontSize="inherit" />, text: route.origem });
  }
  if (suggestions?.travelDate) {
    items.push({
      icon: <EventOutlinedIcon fontSize="inherit" />,
      text: t("bpeWizard.suggestionPlanDate", {
        date: formatDateDisplay(suggestions.travelDate),
      }),
    });
  }
  if (suggestions?.passengerCount > 1) {
    items.push({
      icon: <GroupOutlinedIcon fontSize="inherit" />,
      text: t("bpeWizard.suggestionPlanPassengers", { count: suggestions.passengerCount }),
    });
  }
  if (suggestions?.preferredDepartureTime) {
    items.push({
      icon: <ScheduleOutlinedIcon fontSize="inherit" />,
      text: t("bpeWizard.suggestionPlanTime", { time: suggestions.preferredDepartureTime }),
    });
  }
  if (suggestions?.passenger?.nome) {
    items.push({
      icon: <PersonOutlineOutlinedIcon fontSize="inherit" />,
      text: suggestions.passenger.nome,
    });
  }
  if (suggestions?.paymentMethodName) {
    items.push({
      icon: <PaymentOutlinedIcon fontSize="inherit" />,
      text: suggestions.paymentMethodName,
    });
  }
  return items;
}

/**
 * Barra/cartão de sugestão inferida.
 * Etapa 0: cartão consolidado (resumo + confiança + motivo + "Aplicar tudo").
 * Demais etapas: alerta compacto com a sugestão daquela etapa.
 */
export default function BPeSuggestionBar({ step, suggestions, loading, onApply, applyDisabled, t }) {
  if (loading) {
    return (
      <Box sx={{ mb: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary">
          {t("bpeWizard.suggestionLoading")}
        </Typography>
      </Box>
    );
  }

  if (!suggestions || suggestions.source === "none") return null;

  const isSummary = step === 0;

  if (isSummary) {
    const items = buildPlanItems(suggestions, t);
    if (!items.length) return null;
    const reason = basisText(suggestions, t);
    return (
      <Box
        sx={{
          mb: 0.75,
          flexShrink: 0,
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
          <AutoAwesomeOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700} noWrap>
            {t("bpeWizard.suggestionSmartTitle")}
          </Typography>
          {confidenceChip(suggestions.confidence, t)}
          <Chip
            size="small"
            variant="outlined"
            label={sourceLabel(suggestions.source, t)}
            sx={{ height: 20, fontSize: "0.62rem" }}
          />
          <Box sx={{ flexGrow: 1 }} />
          {onApply && (
            <Tooltip title={t("bpeWizard.suggestionAutoFillHint")}>
              <Button
                size="small"
                variant="contained"
                onClick={onApply}
                disabled={applyDisabled}
                startIcon={<AutoAwesomeOutlinedIcon />}
                sx={{ textTransform: "none", fontWeight: 700, py: 0.25, flexShrink: 0 }}
              >
                {t("bpeWizard.suggestionApplyAll")}
              </Button>
            </Tooltip>
          )}
        </Stack>

        <Stack direction="row" flexWrap="wrap" rowGap={0.25} columnGap={1.5} alignItems="center">
          {items.map((it, idx) => (
            <Stack
              key={idx}
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ fontSize: "0.9rem", color: "text.secondary" }}
            >
              {it.icon}
              <Typography variant="caption" color="text.primary" fontWeight={600}>
                {it.text}
              </Typography>
            </Stack>
          ))}
          {reason && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {`· ${reason}`}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  const message = buildStepMessage(step, suggestions, t);
  if (!message) return null;

  return (
    <Alert
      severity="info"
      icon={<AutoAwesomeOutlinedIcon fontSize="small" />}
      sx={{
        mb: 1,
        flexShrink: 0,
        py: 0.5,
        alignItems: "flex-start",
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
      }}
      action={
        onApply ? (
          <Button
            size="small"
            variant="outlined"
            onClick={onApply}
            disabled={applyDisabled}
            sx={{ textTransform: "none", whiteSpace: "nowrap", mt: 0.25 }}
          >
            {t("bpeWizard.suggestionApply")}
          </Button>
        ) : null
      }
    >
      <Stack spacing={0.5}>
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <Chip
            size="small"
            label={sourceLabel(suggestions.source, t)}
            sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
          />
          {confidenceChip(suggestions.confidence, t)}
        </Stack>
        <Typography variant="caption" component="div" lineHeight={1.35}>
          {message}
        </Typography>
      </Stack>
    </Alert>
  );
}

export { formatDateDisplay as formatSuggestionDateDisplay };
